import { useEffect, useMemo, useState } from 'react';
import { create } from 'zustand';
import { isPsiEmail } from '../utils/psiStaff';
import { API } from '../api/endpoints';

/**
 * Settings that follow PSI staff from one connection to the next (Jeff, 2026-11-04).
 *
 * PSI staff sign in once and move between many customers' connections. Everyone's browser settings
 * are kept per connection user (`dietech_prefs_{userId}_*`, hooks/useUserPreferences), and the
 * PM_Server user id is a different number in every connection, so a PSI user's theme, sidebar,
 * pinned pages, dashboard and favorites were different in each one. For PSI staff only, the
 * connection-neutral ones below are kept per PERSON instead: on the server
 * (GET / PUT api/user/SharedPreferences, DietechSoftwareMain, keyed by the token's email) with a
 * copy in this browser (`dietech_shared_prefs_{email}_*`) so they apply at once on sign-in.
 *
 *  - Turned on at sign-in by AuthTokenSetter (`activateSharedPrefs`) only for a psibuffalo.com
 *    login. Everyone else, and a dev login, keeps the per-connection settings exactly as before.
 *  - Reading: this browser's copy (`readSharedPref`); the server is the source of truth and its
 *    values replace the copy when they arrive (`loadSharedPrefs`).
 *  - Writing: the copy at once, the server shortly after (`writeSharedPref`, writes within
 *    FLUSH_DELAY_MS go up together); a failed write is retried.
 *  - First use: when this browser has no copy yet, the current connection's own values are copied
 *    in; when the server has nothing yet, the copy is sent up. So nobody loses their settings.
 *  - An API without the endpoint (404) or one that refuses the login (403) turns this off again,
 *    and the per-connection settings apply as before.
 *  - Per connection, a PSI user can turn sharing off on the Settings page ("Use my shared settings
 *    here"): that connection then uses its own settings exactly as before sharing, and nothing it
 *    changes is written here. The list of such connections is itself a shared setting
 *    (SHARED_ONLY_KEYS.EXCLUDED_CONNECTIONS), so it follows the person to any computer, and is read
 *    before deciding which settings a connection uses. Turning it back on switches the connection
 *    to the shared settings; its own values are never touched, so turning it off again restores them.
 *  - "Start over from this connection" (startOverFromConnection) replaces the shared settings with
 *    this connection's own.
 *
 * Stays per connection: the default facility, the Residents grid columns (per facility), the
 * sidebar's feature visibility (per facility), and the per-connection recent facilities used at
 * sign-in. The welcome tour and What's New stay per connection user (hooks/useServerUserState).
 */

/** Broadcast when a preference changes in this tab (see useUserPreferences `usePreference`). */
export const PREF_CHANGE_EVENT = 'dietech-pref-changed';
export type PrefChangeDetail = { userId: number; key: string };

/** The connection-neutral settings PSI staff carry across connections (the keys of PREF_KEYS and friends). */
export const SHARED_PREF_KEYS: readonly string[] = [
  // Theme (stores/themeStore.ts)
  'theme.darkMode',
  'theme.size',
  'theme.size.v2',
  'theme.ui2026',
  'theme.highContrast',
  'theme.labelPlacement',
  'theme.font',
  // Reports page
  'reports.viewMode',
  'reports.dashboardLayout',
  // Weight Entry column widths (the columns are the same in every connection)
  'weightEntry.colWidths.v2',
  // Production menu view
  'prodMenu.viewOptions',
  'prodMenu.powerMode',
  'prodMenu.drawerHeight',
  // Layout and navigation (page keys, the same everywhere)
  'layout.sidebarCollapsed',
  'nav.pinned',
  'nav.recent',
  // Dashboard time span
  'dashboard.selectedHours',
  // Help boxes
  'infoBox.hideAll',
  // Guided tours (stores/tourStore.ts)
  'tours.completed',
  'tours.disabled',
];

/** Families of shared keys: every key starting with one of these. */
export const SHARED_PREF_PREFIXES: readonly string[] = [
  'infoBox.collapsed.',
  // Selective Touch display (stores/stDisplayStore.ts)
  'st.display.',
  // Corporate dashboard view choices
  'corporate.',
  // Meal pattern editor: dialog or side panel, docked, width (pages/people/useEditSurfacePref.ts)
  'mealPattern.editSurface',
];

/** Kept only in the shared store: these have their own per-connection home for everyone else. */
export const SHARED_ONLY_KEYS = {
  /** The dashboard's cards and quick actions (per connection: tblPMSec_User.Dashboard_Layout). */
  DASHBOARD_LAYOUT: 'dashboard.layout',
  /** Favorite reports by card slug (per connection: tblPM_User_ReportFavorite). */
  REPORT_FAVORITES: 'reports.favorites',
  /** Facilities used recently in any connection, for the facility switcher. */
  RECENT_FACILITIES: 'psi.recentFacilities',
  /**
   * Connection ids where the PSI user turned sharing off (Settings). Always kept in the shared store,
   * whether sharing is on in the current connection or not.
   */
  EXCLUDED_CONNECTIONS: 'sharing.excludedConnections',
} as const;

const SHARED_ONLY: readonly string[] = Object.values(SHARED_ONLY_KEYS);

/** Whether this preference key follows PSI staff across connections. */
export function isSharedPrefKey(key: string): boolean {
  return SHARED_PREF_KEYS.includes(key)
    || SHARED_ONLY.includes(key)
    || SHARED_PREF_PREFIXES.some((p) => key.startsWith(p));
}

/** Waits this long after a change before sending it, so a burst goes up as one request. */
export const FLUSH_DELAY_MS = 800;
/** A failed send is tried again after this long. */
const RETRY_DELAY_MS = 30_000;
/** The API takes at most this many keys per request (SharedPreferencesController.MaxBatch). */
const MAX_BATCH = 100;

const CACHE_PREFIX = 'dietech_shared_prefs';
const CONNECTION_PREFIX = 'dietech_prefs';

function cachePrefix(email: string): string {
  return `${CACHE_PREFIX}_${email}_`;
}

interface SharedPrefsState {
  /** The PSI login (lower case) whose settings are shared; null = off (per connection, as before). */
  email: string | null;
  /** The connection (PKey_Connection) this tab is in; 0 when not known. */
  connectionId: number;
  /** The PSI user turned sharing off for this connection: it uses its own settings. */
  excluded: boolean;
  /** 'loading' while the server's values are being read; 'ready' once they arrived or failed. */
  status: 'off' | 'idle' | 'loading' | 'ready';
  /** Whether the server answered with its values. */
  available: boolean;
  /** Bumped whenever values change from outside this tab's writes (server values applied, turned off). */
  revision: number;
}

const INITIAL: SharedPrefsState = { email: null, connectionId: 0, excluded: false, status: 'off', available: false, revision: 0 };

export const useSharedPrefsStore = create<SharedPrefsState>(() => ({ ...INITIAL }));

/** The connection user id change events are sent for (usePreference listens per user id). */
let ownerUserId = 0;
/** Changes not yet on the server; null = delete. */
const pending = new Map<string, unknown>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let inflightLoad: Promise<SharedLoadResult> | null = null;
let pageHideHooked = false;

function emit(key: string, userId: number = ownerUserId): void {
  try {
    window.dispatchEvent(new CustomEvent<PrefChangeDetail>(PREF_CHANGE_EVENT, { detail: { userId, key } }));
  } catch {
    // No window (never in the browser).
  }
}

function readCache(email: string, key: string): { found: boolean; value: unknown } {
  try {
    const raw = localStorage.getItem(cachePrefix(email) + key);
    if (raw === null) return { found: false, value: undefined };
    return { found: true, value: JSON.parse(raw) };
  } catch {
    return { found: false, value: undefined };
  }
}

function writeCache(email: string, key: string, value: unknown): void {
  try {
    localStorage.setItem(cachePrefix(email) + key, JSON.stringify(value));
  } catch {
    // Storage full or blocked: the server still gets it.
  }
}

function removeCache(email: string, key: string): void {
  try {
    localStorage.removeItem(cachePrefix(email) + key);
  } catch {
    // ignore
  }
}

/** Every key and value this browser holds for the login. */
function cachedEntries(email: string): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const prefix = cachePrefix(email);
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const name = localStorage.key(i);
      if (!name || !name.startsWith(prefix)) continue;
      const key = name.slice(prefix.length);
      const { found, value } = readCache(email, key);
      if (found) out[key] = value;
    }
  } catch {
    // ignore
  }
  return out;
}

/**
 * First use in this browser: copy the current connection's own values of the shared keys, so
 * turning sharing on changes nothing the person sees.
 */
function seedFromConnection(email: string, userId: number): void {
  const prefix = `${CONNECTION_PREFIX}_${userId}_`;
  try {
    const names: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const name = localStorage.key(i);
      if (name && name.startsWith(prefix)) names.push(name);
    }
    for (const name of names) {
      const key = name.slice(prefix.length);
      if (!isSharedPrefKey(key)) continue;
      const raw = localStorage.getItem(name);
      if (raw === null) continue;
      try {
        writeCache(email, key, JSON.parse(raw));
      } catch {
        // Not JSON: leave it behind.
      }
    }
  } catch {
    // ignore
  }
}

/**
 * Whether the shared settings apply in this connection: a PSI login, and sharing not turned off for
 * this connection. When false, every setting is read and written per connection, as before.
 */
export function sharedPrefsActive(): boolean {
  const s = useSharedPrefsStore.getState();
  return s.email !== null && !s.excluded;
}

/**
 * Whether the shared store is in use for this login at all, even in a connection where the shared
 * settings are turned off. For what is kept there regardless: the turned-off connections themselves
 * and the recent facilities across connections (navigation, not a setting).
 */
export function sharedStoreActive(): boolean {
  return useSharedPrefsStore.getState().email !== null;
}

/** Selector: the shared settings apply in this connection (see sharedPrefsActive). */
export const selectSharedSettingsApply = (s: SharedPrefsState): boolean => s.email !== null && !s.excluded;

/** The connection ids where sharing is turned off. */
export function readExcludedConnections(): number[] {
  const raw = readSharedPref<unknown>(SHARED_ONLY_KEYS.EXCLUDED_CONNECTIONS, []);
  return Array.isArray(raw) ? raw.filter((id): id is number => typeof id === 'number' && id > 0) : [];
}

/**
 * Re-reads whether this connection is turned off. When it changed, every setting may now live
 * somewhere else: bumps the revision (usePreference re-reads) and says so.
 */
function recomputeExcluded(): boolean {
  const s = useSharedPrefsStore.getState();
  const excluded = s.email !== null && s.connectionId > 0 && readExcludedConnections().includes(s.connectionId);
  if (excluded === s.excluded) return false;
  useSharedPrefsStore.setState({ excluded, revision: s.revision + 1 });
  return true;
}

/** The shared value, or `fallback` when there is none (or sharing is off). */
export function readSharedPref<T>(key: string, fallback: T): T {
  const email = useSharedPrefsStore.getState().email;
  if (!email) return fallback;
  const { found, value } = readCache(email, key);
  return found ? (value as T) : fallback;
}

/** Whether a shared value exists for the key. */
export function hasSharedPref(key: string): boolean {
  const email = useSharedPrefsStore.getState().email;
  return !!email && readCache(email, key).found;
}

function hookPageHide(): void {
  if (pageHideHooked || typeof window === 'undefined') return;
  pageHideHooked = true;
  const flushNow = () => { void flushSharedPrefs(); };
  window.addEventListener('pagehide', flushNow);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushNow();
  });
}

function scheduleFlush(delay: number = FLUSH_DELAY_MS): void {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushSharedPrefs();
  }, delay);
}

/** Saves the value here at once and on the server shortly after. Ignored when sharing is off. */
export function writeSharedPref<T>(key: string, value: T, userId: number = ownerUserId): void {
  const email = useSharedPrefsStore.getState().email;
  if (!email) return;
  writeCache(email, key, value);
  pending.set(key, value === undefined ? null : value);
  scheduleFlush();
  emit(key, userId);
}

/** Removes the value here and on the server. Ignored when sharing is off. */
export function removeSharedPref(key: string, userId: number = ownerUserId): void {
  const email = useSharedPrefsStore.getState().email;
  if (!email) return;
  removeCache(email, key);
  pending.set(key, null);
  scheduleFlush();
  emit(key, userId);
}

/**
 * Turns sharing on for a PSI login (or off for anyone else) at sign-in and on every connection
 * pick. Returns whether it is on. Cheap and synchronous: the settings this browser holds apply at
 * once; call `loadSharedPrefs` for the server's.
 */
export function activateSharedPrefs(email: string | null | undefined, userId: number, connectionId = 0): boolean {
  if (!isPsiEmail(email) || userId <= 0) {
    deactivateSharedPrefs();
    return false;
  }
  const normalized = email!.trim().toLowerCase();
  ownerUserId = userId;
  hookPageHide();
  const current = useSharedPrefsStore.getState();
  if (current.email !== normalized) {
    pending.clear();
    inflightLoad = null;
    useSharedPrefsStore.setState({ email: normalized, connectionId, status: 'idle', available: false });
  } else {
    // Another connection picked: read the server again, so a change made in another window or
    // on another computer shows here too.
    useSharedPrefsStore.setState({ connectionId, ...(current.status === 'ready' ? { status: 'idle' as const } : {}) });
  }
  if (Object.keys(cachedEntries(normalized)).length === 0) seedFromConnection(normalized, userId);
  recomputeExcluded();
  return true;
}

/** Whether the shared settings are on for this connection (the Settings switch). */
export function isSharingOnHere(): boolean {
  return !useSharedPrefsStore.getState().excluded;
}

/**
 * The Settings switch "Use my shared settings here". Off: this connection uses its own settings, as
 * before sharing. On: the shared ones (the connection's own are untouched). Saved in the shared
 * store whatever the switch says, so it follows the person. Returns whether anything changed.
 */
export function setSharingOnHere(on: boolean): boolean {
  const s = useSharedPrefsStore.getState();
  if (!s.email || s.connectionId <= 0) return false;
  const list = readExcludedConnections().filter((id) => id !== s.connectionId);
  writeSharedPref(SHARED_ONLY_KEYS.EXCLUDED_CONNECTIONS, on ? list : [...list, s.connectionId]);
  return recomputeExcluded();
}

/** Kept when starting over: the switch list, and the recent facilities (navigation, not settings). */
const KEPT_ON_START_OVER: readonly string[] = [SHARED_ONLY_KEYS.EXCLUDED_CONNECTIONS, SHARED_ONLY_KEYS.RECENT_FACILITIES];

/**
 * "Start over from this connection": the shared settings become this connection's own. Every shared
 * setting is replaced (removed, then this connection user's own values of the shared keys written),
 * plus extra (its dashboard layout and favorite reports, which live in its databases, not in this
 * browser). Kept: the turned-off connections and the recent facilities. Only while the shared
 * settings apply here. Returns whether it ran.
 */
export function startOverFromConnection(userId: number, extra: Record<string, unknown> = {}): boolean {
  const s = useSharedPrefsStore.getState();
  const email = s.email;
  if (!email || s.excluded || userId <= 0) return false;

  const own: Record<string, unknown> = {};
  const prefix = `${CONNECTION_PREFIX}_${userId}_`;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const name = localStorage.key(i);
      if (!name || !name.startsWith(prefix)) continue;
      const key = name.slice(prefix.length);
      if (!isSharedPrefKey(key) || SHARED_ONLY.includes(key)) continue;
      const raw = localStorage.getItem(name);
      if (raw === null) continue;
      try {
        own[key] = JSON.parse(raw);
      } catch {
        // Not JSON: leave it behind.
      }
    }
  } catch {
    // ignore
  }
  for (const [key, value] of Object.entries(extra)) {
    if (value !== undefined && value !== null && !KEPT_ON_START_OVER.includes(key)) own[key] = value;
  }

  for (const key of Object.keys(cachedEntries(email))) {
    if (KEPT_ON_START_OVER.includes(key) || key in own) continue;
    removeCache(email, key);
    pending.set(key, null);
    emit(key, userId);
  }
  for (const [key, value] of Object.entries(own)) {
    writeCache(email, key, value);
    pending.set(key, value);
    emit(key, userId);
  }
  useSharedPrefsStore.setState({ revision: useSharedPrefsStore.getState().revision + 1 });
  scheduleFlush(0);
  return true;
}

/** Back to per-connection settings (a non-PSI sign-in, or an API that refused or lacks the endpoint). */
export function deactivateSharedPrefs(): void {
  const current = useSharedPrefsStore.getState();
  if (flushTimer) clearTimeout(flushTimer);
  if (retryTimer) clearTimeout(retryTimer);
  flushTimer = null;
  retryTimer = null;
  pending.clear();
  inflightLoad = null;
  if (current.email !== null || current.status !== 'off') {
    useSharedPrefsStore.setState({ ...INITIAL, revision: current.revision + 1 });
  }
}

export interface SharedLoadResult {
  /** Some value this browser had was replaced or removed, or sharing was turned off. */
  changed: boolean;
}

/**
 * The API client, loaded when first needed: it pulls in stores and auth helpers that
 * useUserPreferences (which imports this file) must not depend on.
 */
async function client() {
  return (await import('../api/client')).default;
}

function statusOf(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

/**
 * Reads the server's values once per login and applies them over this browser's copy (the server
 * wins, except for changes made here that have not been sent yet). When the server has nothing
 * yet, this browser's copy is sent up: that is how the first connection's settings seed the store.
 * Never throws.
 */
export function loadSharedPrefs(): Promise<SharedLoadResult> {
  const state = useSharedPrefsStore.getState();
  const email = state.email;
  if (!email) return Promise.resolve({ changed: false });
  if (state.status === 'ready') return Promise.resolve({ changed: false });
  if (inflightLoad) return inflightLoad;

  useSharedPrefsStore.setState({ status: 'loading' });
  const run = (async (): Promise<SharedLoadResult> => {
    let values: Record<string, unknown> | null = null;
    try {
      const apiClient = await client();
      const { data } = await apiClient.get<{ available?: boolean; values?: Record<string, unknown> }>(
        API.userSharedPreferences,
        // A website newer than its API (no route yet) must not raise the version-mismatch alarm.
        { _skipVersionMismatch: true } as never,
      );
      if (data && typeof data === 'object' && !Array.isArray(data) && data.available === true) {
        values = data.values && typeof data.values === 'object' && !Array.isArray(data.values) ? data.values : {};
      }
    } catch (err) {
      const status = statusOf(err);
      if (useSharedPrefsStore.getState().email !== email) return { changed: false };
      if (status === 403 || status === 404) {
        // Not PSI by the API's rule, or an API without the endpoint: per connection, as before.
        // This browser's copy goes too, so a later start seeds afresh from the values used meanwhile
        // instead of sending up an old copy.
        for (const key of Object.keys(cachedEntries(email))) removeCache(email, key);
        deactivateSharedPrefs();
        return { changed: true };
      }
      // Not reachable: keep using this browser's copy.
    }
    // Signed in as someone else while this ran: the answer belongs to nobody now.
    if (useSharedPrefsStore.getState().email !== email) return { changed: false };

    let changed = false;
    if (values === null) {
      useSharedPrefsStore.setState({ status: 'ready', available: false });
      return { changed: false };
    }

    const local = cachedEntries(email);
    if (Object.keys(values).length === 0) {
      // Nothing on the server yet: this browser's copy (seeded from the connection) goes up.
      for (const [key, value] of Object.entries(local)) {
        if (!pending.has(key)) pending.set(key, value);
      }
      if (pending.size > 0) scheduleFlush(0);
    } else {
      for (const [key, value] of Object.entries(values)) {
        if (pending.has(key) || value === null || value === undefined) continue;
        const had = local[key];
        if (!(key in local) || JSON.stringify(had) !== JSON.stringify(value)) {
          writeCache(email, key, value);
          emit(key);
          changed = true;
        }
      }
      for (const key of Object.keys(local)) {
        if (key in values || pending.has(key)) continue;
        removeCache(email, key);
        emit(key);
        changed = true;
      }
    }
    // The turned-off connections may have changed elsewhere: read them before anything uses this one.
    if (recomputeExcluded()) changed = true;
    const now = useSharedPrefsStore.getState();
    useSharedPrefsStore.setState({ status: 'ready', available: true, revision: now.revision + (changed ? 1 : 0) });
    return { changed };
  })();
  inflightLoad = run;
  void run.finally(() => {
    if (inflightLoad === run) inflightLoad = null;
  });
  return run;
}

/** Resolves once the server's values have arrived or failed (at once when sharing is off). */
export function whenSharedPrefsSettled(): Promise<void> {
  const { status } = useSharedPrefsStore.getState();
  if (status !== 'loading' || !inflightLoad) return Promise.resolve();
  return inflightLoad.then(() => undefined, () => undefined);
}

/** Sends the changes made since the last send. Never throws; a failure is retried later. */
export async function flushSharedPrefs(): Promise<void> {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  const email = useSharedPrefsStore.getState().email;
  if (!email || pending.size === 0) return;
  const batch = Array.from(pending.entries());
  pending.clear();

  let ok = true;
  try {
    const apiClient = await client();
    for (let i = 0; i < batch.length; i += MAX_BATCH) {
      const values = Object.fromEntries(batch.slice(i, i + MAX_BATCH));
      const { data } = await apiClient.put<{ saved?: boolean }>(API.userSharedPreferences, { values }, {
        _skipVersionMismatch: true,
      } as never);
      if (data?.saved !== true) ok = false;
    }
  } catch (err) {
    const status = statusOf(err);
    if (status === 403 || status === 404) {
      deactivateSharedPrefs();
      return;
    }
    // 400 (a value too big, too many keys): resending will not help.
    ok = status === 400;
  }
  if (ok || useSharedPrefsStore.getState().email !== email) return;
  // Put back what was not sent, unless it changed again since.
  for (const [key, value] of batch) {
    if (!pending.has(key)) pending.set(key, value);
  }
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = setTimeout(() => {
    retryTimer = null;
    void flushSharedPrefs();
  }, RETRY_DELAY_MS);
}

/** Back to nothing (between tests). */
export function resetSharedPrefs(): void {
  deactivateSharedPrefs();
  ownerUserId = 0;
  useSharedPrefsStore.setState({ ...INITIAL });
}

// The test setup resets this between tests without importing it (src/test/setup.ts).
if (import.meta.env.MODE === 'test') {
  (globalThis as { __dietechResetSharedPrefs?: () => void }).__dietechResetSharedPrefs = resetSharedPrefs;
}

/**
 * One shared value as React state: re-renders when it changes (this tab's writes, the server's
 * values arriving, sharing turned off). `undefined` when there is none.
 */
export function useSharedPrefValue<T>(key: string): T | undefined {
  const email = useSharedPrefsStore((s) => s.email);
  const revision = useSharedPrefsStore((s) => s.revision);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<PrefChangeDetail>).detail;
      if (detail?.key === key) setTick((t) => t + 1);
    };
    window.addEventListener(PREF_CHANGE_EVENT, handler);
    return () => window.removeEventListener(PREF_CHANGE_EVENT, handler);
  }, [key]);
  // The stored text, re-read on every change signal; parsed only when it differs, so the value
  // keeps its identity between renders (it feeds useMemo / useEffect dependencies).
  const raw = useMemo(() => {
    void tick;
    void revision;
    if (!email) return null;
    try {
      return localStorage.getItem(cachePrefix(email) + key);
    } catch {
      return null;
    }
  }, [email, key, tick, revision]);
  return useMemo(() => {
    if (raw === null) return undefined;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return undefined;
    }
  }, [raw]);
}
